<!-- <script lang="ts">
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("SidebarSections.vue");
</script> -->

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import AddSection from "./sections/AddSection.vue";
import SectionsList from "./sections/SectionsList.vue";
import EditBlock from "./sections/block/EditBlock.vue";
import { useActions, useGetters, useState } from "@/store/vuex-composition-mappers";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useRoute } from "vue-router";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import type { PageSection } from "@/apps/page/page.interfaces";
import { PAGE_SECTION_KINDS } from "@/apps/page/services/page.service";
import EditTool from "@/apps/page/project/sidebar/sections/tool/EditTool.vue";
import { useToolProjectsStore } from "@/apps/page/project/tool-projects.store";
import { storeToRefs } from "pinia";
import { useEditorActions } from "@/views/tools/page/utils/useEditorActions";
import { usePreviewActions } from "@/views/tools/page/utils/usePreviewActions";

// @ts-expect-error - TODO BF: Fix types
const { removeBlockSection, changeBlockSection, setSidebarMode } = useActions("page", [
    "removeBlockSection",
    "changeBlockSection",
    "setSidebarMode",
]);
// @ts-expect-error - TODO BF: Fix types
const { saveSnapshot } = useActions("page/projects", ["saveSnapshot"]);
const { backupData, setBackupValue } = useBackupData<PageSection>({} as PageSection);
// @ts-expect-error - TODO BF: Fix types
const { getSectionById } = useGetters("page", ["getSectionById"]);
// @ts-expect-error - TODO BF: Fix types
const { sidebarMode } = useState("page", ["sidebarMode"]);

const SIDEBAR_MODES = {
    list: "list",
    addSection: "addSection",
    createBlock: "createBlock",
    editSection: "editSection",
    editBlock: "editBlock",
    editTool: "editTool",
} as const;

const EDIT_BLOCK_DEFAULT_TAB = "content";

const mode = computed({
    get: () => sidebarMode.value,
    set: (value) => setSidebarMode(value),
});

const currentSection = ref<PageSection | null>(null);
const editBlockTab = ref(EDIT_BLOCK_DEFAULT_TAB);

const route = useRoute();
const { cleanQueryParams } = useCleanQueryParams();
const { createToolProject, publishToolProject } = useEditorActions();
const { scrollToSection } = usePreviewActions();

const edit = (section: PageSection, { triggerScrollTo } = { triggerScrollTo: true }) => {
    if (triggerScrollTo) {
        scrollToSection(section.webId);
    }
    if (section.kind === PAGE_SECTION_KINDS.TOOL && !section.projectId) {
        return createToolProject({ tool: section.type, sectionId: section.id });
    }
    if (
        section.kind === PAGE_SECTION_KINDS.TOOL &&
        section.projectId &&
        section.projectData?.access !== "published"
    ) {
        return publishToolProject({ tool: section.type, uuid: section.projectData?.uuid });
    }
    setBackupValue(section);
    currentSection.value = section;
    if (!currentSection.value.styleValues) {
        currentSection.value.styleValues = {};
    }
    mode.value = SIDEBAR_MODES.editBlock;
    editBlockTab.value = EDIT_BLOCK_DEFAULT_TAB;
};

const toolProjectsStore = useToolProjectsStore();
const { toolProjects } = storeToRefs(toolProjectsStore);

watch(
    () => route.query.open,
    (value) => {
        switch (value) {
            case "list":
                mode.value = SIDEBAR_MODES.list;
                setTimeout(() => {
                    cleanQueryParams(["open"]);
                });
                break;
            case "addSection":
                mode.value = SIDEBAR_MODES.addSection;
                break;
            case "editBlock":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id), { triggerScrollTo: false });
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "editSection":
                if (!route.query.id) {
                    return;
                }

                edit(getSectionById.value(+route.query.id), { triggerScrollTo: false });
                setTimeout(() => {
                    cleanQueryParams(["open", "id"]);
                });
                break;
            case "createBlock":
                if (!route.query.id) {
                    return;
                }

                currentSection.value = getSectionById.value(+route.query.id);
                mode.value = SIDEBAR_MODES.createBlock;
                setTimeout(() => {
                    cleanQueryParams(
                        typeof route.query.id === "string" ? ["open", "id"] : ["open"]
                    );
                });
                break;
            default:
                break;
        }
    },
    { immediate: true }
);

const goBack = () => {
    mode.value = SIDEBAR_MODES.list;
    cleanQueryParams(["open"]);
};

const clearBlock = () => {
    currentSection.value = null;
    mode.value = SIDEBAR_MODES.list;
};

const saveBlock = async () => {
    clearBlock();
    await saveSnapshot();
};

const cancelBlock = async () => {
    if (mode.value === SIDEBAR_MODES.createBlock) {
        if (!currentSection.value) return;
        removeBlockSection(currentSection.value.id);
    } else if (mode.value === SIDEBAR_MODES.editBlock) {
        if (!backupData.value) return;
        changeBlockSection(backupData.value as PageSection);
    } else {
        throw new Error("Invalid mode");
    }

    clearBlock();
    await saveSnapshot();
};

onUnmounted(() => {
    mode.value = SIDEBAR_MODES.list;
});
</script>

<template>
    <SectionsList
        v-if="mode === SIDEBAR_MODES.list"
        @addSection="mode = SIDEBAR_MODES.addSection"
        @editSection="edit"
    />

    <EditBlock
        :key="currentSection?.id"
        v-if="
            (mode === SIDEBAR_MODES.createBlock || mode === SIDEBAR_MODES.editBlock) &&
            currentSection !== null &&
            currentSection.kind === PAGE_SECTION_KINDS.BLOCK
        "
        v-model:selectedTab="editBlockTab"
        :section="currentSection"
        @saveBlock="saveBlock"
        @cancelBlock="cancelBlock"
    />
    <EditTool
        :key="currentSection?.id"
        v-if="
            (mode === SIDEBAR_MODES.createBlock || mode === SIDEBAR_MODES.editBlock) &&
            currentSection !== null &&
            currentSection.kind === PAGE_SECTION_KINDS.TOOL
        "
        :section="currentSection"
        :backupData="backupData"
        @save="saveBlock"
        @quit="cancelBlock"
    />
    <!-- BF: A bit hacky but it allows to render all the previews outside of the viewport without blocking the UI and without waiting for the components to be loaded when mode is set to addSection (AddSection is always in the DOM when SidebarSections is mounted) - that's why is not with v-if. Should be defined as last component in the sidebar. -->
    <AddSection
        :class="{ '-translate-x-[9999px]': mode !== SIDEBAR_MODES.addSection }"
        :toolProjects="toolProjects as Record<string, any>"
        @back="goBack"
    />
</template>
